import React, { useState } from 'react';

const dateFmt = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'short',
  timeStyle: 'short'
});
const dayFmt = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium'
});

const formatDate = value => dateFmt.format(new Date(value));
const getRunDate = run => new Date(run.createdAt || run.startedAt);
const getDayKey = date => [date.getFullYear(), date.getMonth(), date.getDate()].join('-');
const groupRunsByDay = runs => {
  const today = new Date();
  const todayKey = getDayKey(today);
  const groups = new Map();

  runs.forEach(run => {
    const date = getRunDate(run);
    const key = getDayKey(date);
    if (!groups.has(key)) groups.set(key, { date, key, runs: [], isToday: key === todayKey });
    groups.get(key).runs.push(run);
  });

  if (!groups.has(todayKey)) groups.set(todayKey, { date: today, key: todayKey, runs: [], isToday: true });

  return Array.from(groups.values()).sort((first, second) => second.date - first.date);
};
const ScriptRunnerHistory = ({
  selectedConfig,
  historyRuns,
  selectedRun,
  currentUserId,
  adminUsersById,
  isLoadingHistory,
  onRefresh,
  onSelectRun,
  onClearSelectedRun
}) => {
  const [runFilter, setRunFilter] = useState('mine');
  const visibleRuns =
    runFilter === 'all'
      ? historyRuns
      : historyRuns.filter(run => currentUserId !== undefined && currentUserId !== null && String(run.TriggeredByUser_id) === String(currentUserId));
  const dayGroups = groupRunsByDay(visibleRuns);

  return (
    <aside className='script-runner-history'>
      <div className='script-runner-history-header'>
        <h2>Run History</h2>
        <button
          type='button'
          className='script-runner-refresh-btn'
          onClick={() => onRefresh(selectedConfig.id)}
          disabled={!selectedConfig || isLoadingHistory}
          aria-label='Refresh run history'
        >
          {isLoadingHistory ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className='script-runner-history-filter' role='group' aria-label='Run history filter'>
        <button type='button' className={runFilter === 'mine' ? 'active' : ''} aria-pressed={runFilter === 'mine'} onClick={() => setRunFilter('mine')}>
          Mine
        </button>
        <button type='button' className={runFilter === 'all' ? 'active' : ''} aria-pressed={runFilter === 'all'} onClick={() => setRunFilter('all')}>
          Everyone
        </button>
      </div>

      {isLoadingHistory && historyRuns.length === 0 ? (
        <div className='script-runner-history-empty'>Loading run history...</div>
      ) : !selectedConfig ? (
        <div className='script-runner-history-empty'>Select a run to view history</div>
      ) : (
        <div className='script-runner-history-list'>
          {dayGroups.map(dayGroup => (
            <details key={dayGroup.key} className='script-runner-history-day' open={dayGroup.isToday || undefined}>
              <summary>{dayGroup.isToday ? 'Today' : dayFmt.format(dayGroup.date)}</summary>
              <div className='script-runner-history-day-runs'>
                {dayGroup.isToday && (
                  <button
                    type='button'
                    className={`script-runner-new-run-option ${!selectedRun ? 'script-runner-is-selected' : ''}`}
                    onClick={onClearSelectedRun}
                    aria-pressed={!selectedRun}
                  >
                    New run
                  </button>
                )}
                {dayGroup.runs.map(run => (
                  <div
                    key={run.id}
                    className={`script-runner-history-card ${selectedRun?.id === run.id ? 'script-runner-is-selected' : ''}`}
                    onClick={() => onSelectRun(run)}
                  >
                    <div className='script-runner-history-card-header'>
                      <span className='script-runner-run-title'>#{run.id}</span>
                      <div className='script-runner-badges'>
                        {Boolean(run.isDryRun) && <span className='script-runner-badge script-runner-badge-dry-run'>Dry Run</span>}
                        <span className={`script-runner-badge script-runner-status-${run.status}`}>{run.status}</span>
                      </div>
                    </div>

                    <div className='script-runner-history-card-body'>
                      <div className='script-runner-meta-line'>
                        <span>{formatDate(run.createdAt || run.startedAt)}</span>
                        {run.TriggeredByUser_id && (
                          <span>
                            {adminUsersById[run.TriggeredByUser_id]?.name ||
                              adminUsersById[run.TriggeredByUser_id]?.email ||
                              `User ID ${run.TriggeredByUser_id}`}
                          </span>
                        )}
                      </div>

                      {run.githubCommit && (
                        <div className='script-runner-meta-line'>
                          <span>
                            Commit:{' '}
                            <a
                              href={`https://github.com/ask-emma/askemma-web/blob/${encodeURIComponent(run.githubCommit)}/node/scripts/${String(
                                selectedConfig.scriptPath
                              ).replace(/^\/+/, '')}`}
                              target='_blank'
                              rel='noreferrer'
                            >
                              <code>{run.githubCommit.slice(0, 7)}</code>
                            </a>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </aside>
  );
};

export default ScriptRunnerHistory;
